<script lang="ts">
    import { tick } from 'svelte';
    import { parseDate, CalendarDate } from '@internationalized/date';
    import dayjs from 'dayjs';

    import {
        findCurrentSelectedFilterLabel,
        isDateSelectionFullMonth,
    } from '$saga/views/Search/utils';
    import type { SearchForm } from '$saga/views/Search/types';
    import { defaultFormValues } from '$saga/views/Search/constants';
    import FilterBadge from './FilterBadge.svelte';
    import { filtersContext } from '$saga/views/Search/store';

    export let searchForm: SearchForm;

    /**
     * When a filter change value, auto submit form
     */
    const handleFormAutoSubmit = async () => {
        // wait for svelte to update value on the DOM
        await tick();
        $filtersContext.isSubmitting = true;
        const form = document.getElementById(
            'travel-search-engine-form',
        ) as HTMLFormElement;
        if (form) {
            form.dispatchEvent(
                new Event('submit', {
                    bubbles: true,
                    cancelable: true,
                }),
            );
        }
    };
</script>

<div
    class="no-scrollbar mx-5xl hidden max-w-layout-max gap-xs overflow-x-auto desktop:flex"
>
    {#if searchForm.destinations}
        {#each searchForm.destinations as dest (dest)}
            {#if String(dest) !== '' && dest != null}
                <FilterBadge
                    label={String(dest) === 'all'
                        ? 'Toutes les destinations'
                        : findCurrentSelectedFilterLabel(
                              String(dest),
                              $filtersContext.defaultDestinationOptions ?? [],
                          )}
                    handleRemoveBadgeFilter={() => {
                        $filtersContext.formValues.destination =
                            $filtersContext.formValues.destination.filter(
                                (d) => d !== dest,
                            );
                        handleFormAutoSubmit();
                    }}
                />
            {/if}
        {/each}
    {/if}

    {#if searchForm.sports}
        {#each searchForm.sports as sport (sport)}
            {#if String(sport) !== '' && sport != null}
                <FilterBadge
                    label={String(sport) === 'all'
                        ? 'Tous les sports'
                        : findCurrentSelectedFilterLabel(
                              String(sport),
                              $filtersContext.defaultSportOptions ?? [],
                          )}
                    handleRemoveBadgeFilter={() => {
                        $filtersContext.formValues.theme =
                            $filtersContext.formValues.theme.filter(
                                (s) => s !== sport,
                            );
                        handleFormAutoSubmit();
                    }}
                />
            {/if}
        {/each}
    {/if}

    {#if searchForm.nbPax && searchForm.nbPax !== 1}
        <FilterBadge
            label={`${searchForm.nbPax} voyageurs`}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.nbPax = defaultFormValues.nbPax;
                // $filtersContext.formValues.adults = defaultFormValues.adults;
                // $filtersContext.formValues.children =
                //     defaultFormValues.children;
                handleFormAutoSubmit();
            }}
        />
    {/if}

    {#if searchForm.startDate && searchForm.endDate}
        {@const start = dayjs(searchForm.startDate.date)}
        {@const end = dayjs(searchForm.endDate.date)}
        <FilterBadge
            label={isDateSelectionFullMonth(
                parseDate(
                    dayjs(searchForm.startDate.date).format('YYYY-MM-DD'),
                ),
                parseDate(dayjs(searchForm.endDate.date).format('YYYY-MM-DD')),
            )
                ? start.format('MMM YYYY')
                : `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.startDate =
                    defaultFormValues.startDate;
                $filtersContext.formValues.endDate = defaultFormValues.endDate;
                $filtersContext.formValues.flexibility =
                    defaultFormValues.flexibility;
                handleFormAutoSubmit();
            }}
        />
    {/if}

    {#each searchForm.levels as level (level)}
        <FilterBadge
            label={findCurrentSelectedFilterLabel(
                String(level),
                $filtersContext.defaultLevels ?? [],
            )}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.levels =
                    $filtersContext.formValues.levels.filter(
                        (l) => l !== level,
                    );
                handleFormAutoSubmit();
            }}
        />
    {/each}

    {#each searchForm.types as type (type)}
        <FilterBadge
            label={findCurrentSelectedFilterLabel(
                String(type),
                $filtersContext.defaultTypes ?? [],
            )}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.types =
                    $filtersContext.formValues.types.filter((t) => t !== type);
                handleFormAutoSubmit();
            }}
        />
    {/each}

    {#each searchForm.tags as tag (tag)}
        <FilterBadge
            label={findCurrentSelectedFilterLabel(
                String(tag),
                $filtersContext.defaultTags ?? [],
            )}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.tags =
                    $filtersContext.formValues.tags.filter((t) => t !== tag);
                handleFormAutoSubmit();
            }}
        />
    {/each}

    {#if (searchForm.budgetMin || searchForm.budgetMax) && (searchForm.budgetMin !== defaultFormValues.budgetMin || searchForm.budgetMax !== defaultFormValues.budgetMax)}
        <FilterBadge
            label={`Entre ${searchForm.budgetMin} et ${searchForm.budgetMax} €`}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.budgetMax =
                    defaultFormValues.budgetMax;
                $filtersContext.formValues.budgetMin =
                    defaultFormValues.budgetMin;
                handleFormAutoSubmit();
            }}
        />
    {/if}

    {#if (searchForm.durationMin || searchForm.durationMax) && (searchForm.durationMin !== defaultFormValues.durationMin || searchForm.durationMax !== defaultFormValues.durationMax)}
        <FilterBadge
            label={`Entre ${searchForm.durationMin} et ${searchForm.durationMax} jours`}
            handleRemoveBadgeFilter={() => {
                $filtersContext.formValues.durationMin =
                    defaultFormValues.durationMin;
                $filtersContext.formValues.durationMax =
                    defaultFormValues.durationMax;
                handleFormAutoSubmit();
            }}
        />
    {/if}
</div>
